import React, { useMemo, useState } from "react";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { getQuoteFieldLabel, isCompanyQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";

const FIELD_TYPES = [
  { id: "text", label: "Text" },
  { id: "number", label: "Number" },
  { id: "dropdown", label: "Dropdown" },
];

const OPTION_COUNTS = [1, 2, 3];

const REQUIRED_TEMPLATE_FIELDS = [
  { id: "company", label: "Company", field_type: "dropdown", dropdown_options: [] },
  { id: "product", label: "Product", field_type: "dropdown", dropdown_options: [] },
];

export default function QuoteTemplateBuilder() {
  usePageTitle("Quote Templates");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get("returnTo") || "/app/appointments";

  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState("Mortgage Protection");
  const [optionCount, setOptionCount] = useState("3");
  const [fields, setFields] = useState(() => [...REQUIRED_TEMPLATE_FIELDS]);

  const hasFields = useMemo(() => fields.length > 0, [fields]);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        id: `field-${Date.now()}`,
        label: "",
        field_type: "text",
        dropdown_options: [],
      },
    ]);
  };

  const updateField = (id, patch) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== id) return field;
        const label = getQuoteFieldLabel(field);
        const isRequired = isCompanyQuoteField(label) || isProductQuoteField(label);
        if (!isRequired) return { ...field, ...patch };
        return {
          ...field,
          label: isCompanyQuoteField(label) ? "Company" : "Product",
          field_type: "dropdown",
          dropdown_options: [],
        };
      })
    );
  };

  const removeField = (id) => {
    setFields((prev) =>
      prev.filter((field) => {
        if (field.id !== id) return true;
        const label = getQuoteFieldLabel(field);
        return !(isCompanyQuoteField(label) || isProductQuoteField(label));
      })
    );
  };

  const handleSave = async () => {
    try {
      const normalizedFields = (() => {
        const list = [...fields];
        const hasCompany = list.some((f) => isCompanyQuoteField(getQuoteFieldLabel(f)));
        const hasProduct = list.some((f) => isProductQuoteField(getQuoteFieldLabel(f)));
        if (!hasCompany) list.unshift({ ...REQUIRED_TEMPLATE_FIELDS[0] });
        if (!hasProduct) list.push({ ...REQUIRED_TEMPLATE_FIELDS[1] });
        return list.map((f) => {
          const label = getQuoteFieldLabel(f);
          if (isCompanyQuoteField(label)) {
            return { ...f, label: "Company", field_type: "dropdown", dropdown_options: [] };
          }
          if (isProductQuoteField(label)) {
            return { ...f, label: "Product", field_type: "dropdown", dropdown_options: [] };
          }
          return f;
        });
      })();

      const payload = {
        name: templateName,
        option_count: Number(optionCount),
        fields: (normalizedFields || []).map((f) => ({
          id: f.id,
          label: f.label,
          field_type: f.field_type,
          dropdown_options: f.field_type === "dropdown" ? (f.dropdown_options || []).filter(Boolean) : [],
        })),
      };

      const res = await api.post("/quote-templates", payload);
      setTemplateId(res.data?.id || null);
      toast({ title: "Template saved" });

      navigate(returnTo);
    } catch {
      toast({ title: "Unable to save template", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 pb-10" data-testid="quote-template-builder-page">
      <Card className="glass">
        <CardContent className="space-y-6 p-6">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-white">Quote Template Builder</h2>
            <p className="text-sm text-slate-200/70">
              Create fields and build a quote form template.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-white">Quote Template Builder</h2>
              <p className="text-sm text-slate-200/70">Create fields and build a quote form template.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
                onClick={() => navigate(returnTo)}
                data-testid="quote-template-back"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                type="button"
                className="rounded-full bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/30 hover:bg-amber-500/30"
                onClick={handleSave}
                data-testid="quote-template-save"
              >
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-slate-200/80">Template Name</Label>
              <Input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder="Template name"
                className="h-11 bg-white/5 text-white"
                data-testid="quote-template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200/80">Options</Label>
              <Select value={optionCount} onValueChange={setOptionCount}>
                <SelectTrigger className="h-11 bg-white/10 text-slate-100">
                  <SelectValue placeholder="Options" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {OPTION_COUNTS.map((c) => (
                    <SelectItem key={c} value={String(c)}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-200/80">Fields</Label>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
                onClick={addField}
                data-testid="quote-template-add-field"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Field
              </Button>
            </div>

            {!hasFields ? (
              <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-slate-200/60">
                No fields yet. Add the first field to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field) => {
                  const label = getQuoteFieldLabel(field);
                  const isRequired = isCompanyQuoteField(label) || isProductQuoteField(label);

                  return (
                    <div key={field.id} className="space-y-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                      <div className="flex flex-wrap items-center gap-3">
                        <Input
                          value={field.label}
                          onChange={(event) => updateField(field.id, { label: event.target.value })}
                          placeholder="Field label"
                          className="h-11 flex-1 bg-white/5 text-white"
                          readOnly={isRequired}
                          data-testid={`quote-template-field-label-${field.id}`}
                        />
                        <Select
                          value={field.field_type}
                          onValueChange={(value) =>
                            updateField(field.id, {
                              field_type: value,
                              dropdown_options: value === "dropdown" ? field.dropdown_options || [""] : [],
                            })
                          }
                          disabled={isRequired}
                        >
                          <SelectTrigger className="h-11 w-44 bg-white/10 text-slate-100">
                            <SelectValue placeholder="Field type" />
                          </SelectTrigger>
                          <SelectContent className="glass border-white/10">
                            {FIELD_TYPES.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-11 w-11 rounded-full text-slate-300 hover:text-white"
                          onClick={() => removeField(field.id)}
                          disabled={isRequired}
                          data-testid={`quote-template-field-remove-${field.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        {isRequired ? (
                          <span
                            className="rounded-full bg-emerald-500/15 px-2 py-1 text-xs text-emerald-100 ring-1 ring-emerald-400/20"
                            data-testid={`quote-template-required-${field.id}`}
                          >
                            Required
                          </span>
                        ) : null}
                      </div>

                      {field.field_type === "dropdown" && !isRequired ? (
                        <div className="space-y-2">
                          <Label className="text-xs text-slate-200/70">Dropdown options</Label>
                          <div className="space-y-2">
                            {(field.dropdown_options || []).map((opt, idx) => (
                              <Input
                                key={`${field.id}-opt-${idx}`}
                                value={opt}
                                onChange={(e) => {
                                  const next = [...(field.dropdown_options || [])];
                                  next[idx] = e.target.value;
                                  updateField(field.id, { dropdown_options: next });
                                }}
                                placeholder={`Option ${idx + 1}`}
                                className="h-11 bg-white/5 text-white"
                              />
                            ))}
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
                            onClick={() => updateField(field.id, { dropdown_options: [...(field.dropdown_options || []), ""] })}
                            data-testid={`quote-template-add-option-${field.id}`}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Option
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
              onClick={() => setFields([...REQUIRED_TEMPLATE_FIELDS])}
              data-testid="quote-template-clear-fields"
            >
              Clear Fields
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}